import { useEffect } from 'react'

const EmployeeQuestionsQuestion = ({ formData, setFormData, questionState, setQuestionState, otherText, setOtherText }) => {

    useEffect(() => {
        const existingResponses = formData.form_responses?.question6?.responses || [];
        if (existingResponses.length > 0) {
            const newState = { ...questionState };

            existingResponses.forEach(response => {
                if (response.answer_text === otherQuestion && response.text_response) {
                    setOtherText(response.text_response);
                } else if (newState[response.answer_text]) {
                    newState[response.answer_text].selected = true;
                }
            });

            setQuestionState(newState);
        }
    }, [formData.form_responses?.question6?.responses])

    const getSelectedCount = () => {
        return Object.values(questionState).filter(q => q.selected).length;
    };

    const isAtLimit = () => {
        return getSelectedCount() >= 3;
    };

    const handleQuestionUpdate = (responseKey, checkedValue, parentKey = null, dataKey = null, value = null) => {
        if (checkedValue && !hasResponse(responseKey) && isAtLimit()) {
            return;
        }

        setQuestionState(prevState => {
            const newState = { ...prevState };

            if (dataKey) {
                newState[responseKey] = {
                    ...newState[responseKey],
                    [dataKey]: value
                };
            } else {
                newState[responseKey] = {
                    ...newState[responseKey],
                    selected: checkedValue
                };
            }

            return newState;
        });

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses?.question6?.responses || [];

            if (dataKey) {
                const updatedResponses = currentResponses.map(response =>
                    response.answer_text === responseKey
                        ? { ...response, [dataKey]: value }
                        : response
                );

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question6: {
                            question_title: "What questions would you want us to ask the employee?",
                            responses: updatedResponses
                        }
                    }
                };
            } else {
                let updatedResponses = [...currentResponses];

                if (checkedValue) {
                    if (!updatedResponses.find(r => r.answer_text === responseKey)) {
                        updatedResponses.push({
                            answer_text: responseKey,
                            sub_responses: []
                        });
                    }
                } else {
                    updatedResponses = updatedResponses.filter(r => r.answer_text !== responseKey);
                }

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question6: {
                            question_title: "What questions would you want us to ask the employee?",
                            responses: updatedResponses
                        }
                    }
                };
            }
        });
    };

    const handleOtherTextChange = (value) => {
        setOtherText(value);

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses?.question6?.responses || [];
            let updatedResponses = [...currentResponses];

            updatedResponses = updatedResponses.filter(r => r.answer_text !== otherQuestion);

            if (value && value.trim() !== '') {
                updatedResponses.push({
                    answer_text: otherQuestion,
                    text_response: value,
                    sub_responses: []
                });
            }

            return {
                ...prevFormData,
                form_responses: {
                    ...prevFormData.form_responses,
                    question6: {
                        question_title: "What questions would you want us to ask the employee?",
                        responses: updatedResponses
                    }
                }
            };
        });
    };

    const hasResponse = (responseKey) => {
        return questionState[responseKey]?.selected || false;
    };

    const getResponseData = (responseKey, dataKey) => {
        return questionState[responseKey]?.[dataKey] || '';
    };

    const questions = [
        'Is the employee fit for their normal hours and duties required by their post? If not, when will they be able to return to work/return to normal hours/duties or alternative work?',
        'What if any work adjustments may facilitate a return to the substantive role?',
        'If the substantive role cannot be adjusted sufficiently what temporary alternative duties is the employee fit to undertake safely?',
        'Is there an underlying medical condition affecting their ability to work?',
        'Has the employee accessed appropriate treatment/support to support their health, wellbeing and attendance?',
        'Is there any additional help or treatment recommended to support the employee?',
        'What lifestyle adjustments if any are recommended for the employee to support their ongoing health, wellbeing and work attendance?',
        'Is the employee fit for work in any capacity? If not when is the employee likely to be fit to return to work?',
        'Is this employee likely to be able to provide regular and effective service in the future?',
        'If the employee is unlikely to return to their substantive post for health reasons but can work in an alternative role is permanent redeployment recommended?',
        'If the employee is unlikely to return to their substantive post for health reasons is the employee a candidate for ill health retirement application?',
        'In your view, is the employee likely to be covered under the disability provision of the Equality Act 2010? Occupational health can provide guidance only on this question.',
        'Is the employee fit to attend a formal meeting (i.e. disciplinary)?'
    ];

    const otherQuestion = 'Other';

    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                    Step 7: What questions would you want us to ask the employee?
                </h2>
                <div className="flex flex-col items-end">
                    <span className="text-red-500 text-sm font-medium">Required *</span>
                    <span className="text-xs text-gray-600 mt-1">
                        {getSelectedCount()}/3 questions selected
                    </span>
                </div>
            </div>
            <div className="p-4">
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                Please select up to three questions from the below list that you wish us to ask in the assessment
                            </p>
                        </div>
                    </div>
                </div>

                {isAtLimit() && (
                    <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-md">
                        <p className="text-sm text-yellow-700">
                            You have selected the maximum of 3 questions. Uncheck a question to select a different one.
                        </p>
                    </div>
                )}

                <div className="space-y-3">
                    {questions.map((question, index) => {
                        const isSelected = hasResponse(question);
                        const isDisabled = !isSelected && isAtLimit();

                        return (
                            <div key={index} className={isDisabled ? 'opacity-50' : ''}>
                                <div className="flex items-start">
                                    <input
                                        type="checkbox"
                                        id={`employee_question_${index}`}
                                        value={question}
                                        className={`mt-1 mr-3 flex-shrink-0 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        onChange={(e) => handleQuestionUpdate(question, e.target.checked)}
                                        checked={isSelected}
                                        disabled={isDisabled}
                                    />
                                    <label
                                        htmlFor={`employee_question_${index}`}
                                        className={`text-sm leading-relaxed ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}
                                    >
                                        {question}
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 pt-6">
                    <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    You may add up to three more bespoke questions in the text box below in addition to the selected questions above
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <textarea
                            id="employee_question_other_textarea"
                            name="employee_question_other_textarea"
                            rows="3"
                            className="border rounded-md py-2 px-4 w-full resize-vertical"
                            placeholder="Please provide details of any additional questions you would like us to ask..."
                            onChange={(e) => handleOtherTextChange(e.target.value)}
                            value={otherText}
                        ></textarea>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeQuestionsQuestion;